import { InfoNotice } from '@/components/patterns/info-notice'
import { Can } from '@/lib/casl'
import { recoveryControls } from '@/lib/services/stripe-connect/stripe-migration.schema'

function MigrationStatusNotice({ migration, action, canManageCustomers }) {
  if (migration.ownership_blocked) {
    return (
      <InfoNotice title="Stripe upgrade is blocked" tone="warning">
        Payment ownership or customer account associations need attention before this shop can
        upgrade. Review the payment ownership backfill results and resolve the remaining blockers.
        The account upgrade remains on hold.
      </InfoNotice>
    )
  }
  const recovery = migration.recovery
  if (!recovery) return null
  const { step, readers } = recoveryControls(recovery)
  if (step === 'complete') return null
  const checkingCopy =
    step === 'copy' && ['parsing', 'validating', 'coverage'].includes(recovery.import?.status)
  const requiresAction = migration.requires_action && step !== 'account-access'
  const copyIssue = step === 'copy' && canManageCustomers ? recovery.copy_issue : null

  const title = requiresAction
    ? 'New Stripe account needs attention'
    : copyIssue
      ? copyIssue.title
      : checkingCopy
        ? 'Checking copied payment methods'
        : {
            'account-access': 'Payment account access needs attention',
            'account-readiness': 'Finish payment account setup',
            checking: 'Checking migration requirements',
            copy:
              recovery.saved_cards === undefined
                ? 'Customer recovery is in progress'
                : recovery.saved_cards
                  ? 'Saved payment method recovery'
                  : 'Subscription payment recovery',
            readers: 'Card readers need to be reconnected',
            finishing: 'Final validation is running',
          }[step]

  return (
    <InfoNotice
      title={title}
      tone={
        requiresAction || copyIssue
          ? 'danger'
          : step === 'account-access' || recovery.last_error
            ? 'warning'
            : 'info'
      }
      action={action}
    >
      {requiresAction ? (
        <>Stripe needs additional information. Review the new account’s requirements. </>
      ) : copyIssue ? (
        <>{copyIssue.message} Review the recovery instructions before continuing. </>
      ) : step === 'copy' && !checkingCopy && recovery.saved_cards !== undefined ? (
        <>
          Copy customers and{' '}
          {recovery.saved_cards ? 'saved payment methods' : 'subscription payment details'}
          {recovery.subscriptions
            ? ` (${recovery.subscriptions} ${recovery.subscriptions === 1 ? 'subscription' : 'subscriptions'})`
            : ''}{' '}
          in Stripe. We’ll retrieve and validate the copy results automatically.
        </>
      ) : step === 'copy' && recovery.saved_cards === undefined ? (
        <>
          The platform team is handling customer recovery. Reader setup follows when it’s
          complete.{' '}
        </>
      ) : checkingCopy ? (
        <>The copy result is being validated. Reader checks follow automatically. </>
      ) : step === 'readers' ? (
        <>
          Readers to verify or reconnect: {readers.length}. Contact the shop to reconnect them. The
          account switches automatically after the final checks pass.{' '}
        </>
      ) : step === 'account-readiness' ? (
        <>The new account must accept card payments and have its payment domains configured. </>
      ) : step === 'account-access' ? (
        <>Restore account access with support before continuing. </>
      ) : step === 'checking' ? (
        <>Background checks are identifying the next recovery step. </>
      ) : step === 'finishing' ? (
        <>Final safety checks are running automatically. No manual activation is needed. </>
      ) : null}{' '}
      {migration.activated
        ? 'New payments stay on the upgraded account while staff finishes this follow-up.'
        : 'Payments stay on the existing account until the remaining steps and checks are complete.'}
    </InfoNotice>
  )
}

export default function StripeMigrationStatusNotice({ migration, action, fallback = null }) {
  return (
    <Can I="access" a="global" passThrough>
      {(isGlobal) => (
        <Can I="manage" a="stripe-migrations" passThrough>
          {(canManage) =>
            isGlobal && canManage && (migration?.ownership_blocked || migration?.recovery) ? (
              <Can I="manage" a="stripe-migration-customers" passThrough>
                {(canManageCustomers) => (
                  <MigrationStatusNotice
                    migration={migration}
                    action={action}
                    canManageCustomers={canManageCustomers}
                  />
                )}
              </Can>
            ) : (
              fallback
            )
          }
        </Can>
      )}
    </Can>
  )
}
